import {
  BudgetApplicationAnswer,
  IApplicableBudget,
  IBudget,
  IBudgetApplication,
  IBudgetApplicationTemplate,
  IBudgetEmployeeChoice,
  IBudgetExpense,
  IBudgetMemberInfo,
  IBudgetSummary,
  IBudgets,
  IEmployeeBudget,
  IMenhirApplicableBudget,
  IMenhirBudget,
  IMenhirBudgetApplication,
  IMenhirBudgetApplicationTemplate,
  IMenhirBudgetEmployeeChoice,
  IMenhirBudgetExpense,
  IMenhirBudgetMemberInfo,
  IMenhirBudgetSummary,
  IMenhirEmployeeBudget,
} from '@/common/integrations/menhir/employee/budgets/budgets.types';
import { IMenhirPaginationResponse } from '@/common/types/menhir/general';

export const transformBudget = (budget: IMenhirBudget): IBudget => ({
  description: budget.description,
  endedAt: budget.ended_at,
  id: budget.id,
  logo: budget.logo,
  name: budget.name,
  recurrence: budget.recurrence,
  startedAt: budget.started_at,
  status: budget.status,
  withAccrual: budget.with_accrual,
  renewalDate: budget.renewal_date,
  paymentMethod: budget.payment_method,
  reimbursementRequiredFiles: budget.reimbursement_required_files,
});

export const transformBudgetEmployee = (
  budget: IMenhirEmployeeBudget
): IEmployeeBudget => ({
  id: budget.id,
  startedAt: budget.started_at,
  budget: transformBudget(budget.budget),
  endedAt: budget.ended_at,
  budgetAmount: budget.budget_amount,
  pocket: budget.pocket
    ? {
        availableBalance: budget.pocket.available_balance,
        retainedBalance: budget.pocket.retained_balance,
        id: budget.pocket.id,
      }
    : undefined,
  pocketId: budget.pocket_id,
  spentAmount: budget.spent_amount,
  status: budget.status,
  displayName: budget.display_name,
  accumulatedAmount: budget.accumulated_amount,
  firstTopUpDate: budget.first_top_up_date,
  firstTopUpAlreadyBeenMade: budget.first_top_up_already_been_made,
  applicationTemplate: budget.application_template
    ? {
        singleApplication: budget.application_template.single_application,
      }
    : undefined,
  formId: budget.form_id,
  supportFilesEnabled: budget.support_files_enabled,
});

export const transformBudgets = (
  budget_employees: IMenhirPaginationResponse<IMenhirEmployeeBudget>
): IBudgets => {
  return {
    currentPage: budget_employees.current_page,
    resultsPerPage: budget_employees.results_per_page,
    totalPages: budget_employees.total_pages,
    list: budget_employees.list.map(transformBudgetEmployee),
    totalResults: budget_employees.total_results,
  };
};

export const transformBudgetSummary = (
  budget_employees: IMenhirBudgetSummary
): IBudgetSummary => ({
  totalResults: budget_employees.total_results,
  activeSummary: {
    availableBalance: budget_employees.active_summary.available_balance,
    numResults: budget_employees.active_summary.num_results,
  },
  currentPage: budget_employees.current_page,
  resultsPerPage: budget_employees.results_per_page,
  totalPages: budget_employees.total_pages,
});

export const transformBudgetExpense = (
  expense: IMenhirBudgetExpense
): IBudgetExpense => ({
  id: expense.id,
  description: expense.description,
  spentAt: expense.spent_at,
  amount: expense.amount,
  files: expense.files,
  budget: expense.budget,
  employee: {
    id: expense.employee.id,
    name: expense.employee.name,
    taxId: expense.employee.tax_id,
  },
});

export const transformBudgetApplication = (
  budgetApplication: IMenhirBudgetApplication
): IBudgetApplication => {
  return {
    id: budgetApplication.id,
    budget: {
      id: budgetApplication.budget.id,
      name: budgetApplication.budget.name,
      logo: budgetApplication.budget.logo,
    },
    insertedAt: budgetApplication.inserted_at, // TODO: shouldn't be optional
    status: budgetApplication.status,
    name: budgetApplication.name,
    answers:
      budgetApplication.answers?.map((ans) => {
        return {
          fieldId: ans.field_id,
          id: ans.id,
          inputType: ans.input_type,
          answer: ans.answer,
          label: ans.label,
        } as BudgetApplicationAnswer;
      }) ?? [],
    rejectedOn: budgetApplication.rejected_at,
    rejectionReason: budgetApplication.rejection_reason,
    cancelledOn: budgetApplication.cancelled_at,
  };
};

export const transformApplicationTemplate = (
  applicationTemplate: IMenhirBudgetApplicationTemplate,
  applicationsCount: number
): IBudgetApplicationTemplate => ({
  budget: transformBudget(applicationTemplate.budget),
  description: applicationTemplate.description,
  fields: applicationTemplate.fields.map((field) => ({
    id: field.id,
    label: field.label,
    type: field.type,
    hint: field.hint,
    inputType: field.input_type,
    options: field.options,
    required: field.required,
  })),
  amount: applicationTemplate.amount,
  singleApplication: applicationTemplate.single_application,
  applicationsCount,
});

export const transformApplicableBudget = (
  applicableBudget: IMenhirApplicableBudget
): IApplicableBudget => ({
  applicationsCount: applicableBudget.applications_count,
  budget: transformBudget(applicableBudget.budget),
  budgetAmount: applicableBudget.budget_amount,
  applicationTemplate: transformApplicationTemplate(
    applicableBudget.application_template,
    applicableBudget.applications_count
  ),
});

export const transformBudgetEmployeeChoice = (
  budgetEmployeeChoice: IMenhirBudgetEmployeeChoice
): IBudgetEmployeeChoice => ({
  id: budgetEmployeeChoice.id,
  name: budgetEmployeeChoice.name,
  availableBalance: budgetEmployeeChoice.available_balance,
  pocketId: budgetEmployeeChoice.pocket_id,
  budgetId: budgetEmployeeChoice.budget_id,
  reimbursementRequiredFiles: budgetEmployeeChoice.reimbursement_required_files,
  formId: budgetEmployeeChoice.form_id,
  supportFilesEnabled: budgetEmployeeChoice.support_files_enabled,
});

export const transformBudgetMemberInfo = (
  familyMember: IMenhirBudgetMemberInfo
): IBudgetMemberInfo => ({
  applicationName: familyMember.application.name,
  registered: familyMember.registered,
  member: transformBudgetMember(familyMember.member),
});

export const transformBudgetMember = (
  familyMember: IMenhirBudgetMemberInfo['member']
): IBudgetMemberInfo['member'] => ({
  taxId: {
    type: familyMember.tax_id.type,
    value: familyMember.tax_id.value,
  },
  birthDate: familyMember.birth_date,
  name: familyMember.name,
});
